import { useHistory } from "react-router-dom";
import DetailRenderer from "../components/global/forms/DetailRenderer";
import Button from "../components/organism/Button";
import ErrorBoundary from "../components/global/ErrorBoundary";
import useProfile from "../services/utils/custom hooks/useProfile";
import AdminContentLayout from "../layouts/AdminContentLayout";
import { useEffect, useState } from "react";
import AssociatedOrganizationAndRolesTable from "../components/Tables/AssociatedOrganizationAndRoles";
import utils from "../services/utils";
import UserDetailLoadingIndicator from "../components/global/LoadingIndicators/UserDetailLoadingIndicator";
import CustomText from "../components/atomic/CustomText";
import useSignature from "../services/utils/custom hooks/eSignature/useSignature";
import SignatureViewer from "../components/global/SignatureViewer";
import CustomPopUp from "../components/global/CustomPopup";
import CustomSignatureInput from "../components/global/CustomSignatureInput";
import useUpdateEsignature from "../services/utils/custom hooks/eSignature/useUpdateEsignature";
import useCreateSignature from "../services/utils/custom hooks/eSignature/useCreateSignature";
import useDeleteSignature from "../services/utils/custom hooks/eSignature/useDeleteSignature";
import { formatPhoneForDisplay } from "../utils/helpers/phoneNumberHelpers";
import { usePhoneVerificationStatus } from "../services/utils/custom hooks/PhoneVerification/usePhoneVerification";
import { IS_PERSONAL_NOTIFICATION_SETTINGS_ENABLED } from "../constants/notificationSettingsFlags";
import { IS_MFA_SOFT_LAUNCH_ENABLED } from "../constants/mfaFlags";
import PhoneVerificationPopup from "../components/popups/phoneVerificationPopup";
import MfaSetupPopup from "../components/popups/mfaSetupPopup";
import { useMfaEnrollmentState } from "../services/utils/custom hooks/Mfa/useMfaFactors";

function Profile(): JSX.Element {
  const history = useHistory();
  const { data, isLoading, isError, isFetching } = useProfile();
  const { data: phoneStatus } = usePhoneVerificationStatus();
  const {
    enrolled: mfaEnrolled,
    statusDescription: mfaStatusDescription,
    enrolledMethodLabels,
    missingMfaMethod,
    isLoading: mfaLoading,
  } = useMfaEnrollmentState();
  const { data: signatureData, isLoading: fetchingSignatureData } =
    useSignature();

  const [showSignaturePopup, setShowSignaturePopup] = useState<boolean>(false);
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [showMfaEnroll, setShowMfaEnroll] = useState(false);
  const { mutate, isPending, isSuccess } = useUpdateEsignature();
  const {
    mutate: createSignature,
    isPending: creatingSignature,
    isSuccess: createdSuccessfully,
  } = useCreateSignature();

  // const [detail, setUserDetail] = useState({
  //   logo: data?.user?.imageUrl,
  //   fullName: `${data?.user?.firstName}  ${data?.user?.lastName}`,
  //   email: data?.user?.email,
  //   role: data?.user?.roles.join(", "),
  // });
  const mobilePhone =
    phoneStatus?.mobilePhone ||
    data?.user?.mobilePhone ||
    data?.user?.primaryPhone ||
    "";
  const detail = {
    logo: data?.user?.imageUrl || data?.user?.profileImage,
    fullName: `${data?.user?.firstName}  ${data?.user?.lastName}`,
    email: data?.user?.email,
    mobilePhone: (() => {
      if (!mobilePhone) return "---";
      const display = formatPhoneForDisplay(mobilePhone);
      if (phoneStatus?.mobilePhoneVerified) return `${display} (Verified)`;
      return `${display} (Unverified)`;
    })(),
    role: data?.user?.roles?.join(", "),
    ...(data?.user?.roles?.includes("Physician") && {
      suffix: data.user.suffix,
      specialty: data.user.specialty,
      npi: data.user.npi,
    }),
  };
  const {
    mutate: deleteSignature,
    isPending: deletingSignature,
    isSuccess: deletedSuccessfully,
  } = useDeleteSignature({
    closePopup: () => {
      setShowDeletePopup(false);
    },
  });
  const [showDeletePopup, setShowDeletePopup] = useState<boolean>(false);
  const isPhysician = data?.user?.roles?.includes?.("Physician");
  // const [userNotificationMediumTableBody, setUserNotificationMediumTableBody] =
  //   useState<Array<BodyColumnData[]>>([]);
  // const [
  //   userNotificationMediumTableColumn,
  //   setUserNotificationMediumTableColumn,
  // ] = useState<HeaderColumnData[]>([]);

  useEffect(() => {
    setShowSignaturePopup(false);
  }, [isSuccess, createdSuccessfully]);

  function handleSubmission(signatureUrl: string) {
    if (!!signatureData?.id)
      mutate({
        userId: data?.user?.id,
        base64URL: signatureUrl,
        fileName: `${data?.user?.id}_${data?.user?.firstName}_signature_file.png`,
      });
    else
      createSignature({
        userId: data?.user?.id,
        base64URL: signatureUrl,
        fileName: `${data?.user?.id}_${data?.user?.firstName}_signature_file.png`,
      });
  }

  // useEffect(() => {
  //   if (!isLoading) {
  //     if (data.user.roles.includes("Physician")) {
  //       setUserDetail((prevState) => ({
  //         ...prevState,
  //         suffix: data.user.suffix,
  //         specialty: data.user.specialty,
  //         npi: data.user.npi,
  //       }));
  //     }
  //   }
  // }, [isLoading]);
  return (
    <AdminContentLayout
      pageTitle="Profile | CSTL"
      subHeaderTitle="My Profile"
      rightSectionClassName="xs:overflow-x-hidden md:overflow-x-visible"
      customButtons={[
        {
          buttonTheme: "primary",
          textInformation: {
            text: "Edit",
            fontWeight: "font-bold",
          },
          className: "hidden md:block md:mx-5",
          onClick: () => {
            history.push("/access/profile/editprofile");
          },
        },
      ]}
    >
      <ErrorBoundary>
        {isLoading || isFetching || fetchingSignatureData ? (
          <UserDetailLoadingIndicator />
        ) : (
          <div className=" flex flex-col xs:items-stretch md:items-start overflow-auto">
            <div className="w-full overflow-y-auto overflow-x-hidden">
              <div className="relative w-full xs:px-3 md:px-5 ">
                <Button
                  buttonTheme="primary"
                  textInformation={{
                    text: "Edit",
                    fontWeight: "font-bold",
                  }}
                  className="self-end absolute right-3 top-5 md:hidden"
                  onClick={() => {
                    history.push("/access/profile/editprofile");
                  }}
                />
                <DetailRenderer
                  data={detail}
                  groupFields={
                    isPhysician
                      ? [
                          "logo",
                          "fullName",
                          "email",
                          "mobilePhone",
                          "role",
                          "npi",
                          "suffix",
                          "specialty",
                        ]
                      : ["logo", "fullName", "email", "mobilePhone", "role"]
                  }
                  hiddenFields={[]}
                  roundedImage
                  showLoadingIndication={isLoading}
                  actions={
                    <div className="flex flex-wrap md:items-center gap-x-6 gap-y-2 mt-2 mb-4">
                      <Button
                        buttonTheme="transparent"
                        textInformation={{
                          text: !mobilePhone
                            ? "Add Phone"
                            : phoneStatus?.mobilePhoneVerified
                              ? "Edit Phone"
                              : "Verify Phone",
                          fontWeight: "font-semibold",
                        }}
                        className="text-primary xs:text-left !px-0"
                        onClick={() => setShowPhoneModal(true)}
                      />
                      <div className="hidden md:block h-8 border-r border-gray-300" />
                      <Button
                        buttonTheme="transparent"
                        textInformation={{
                          text: "Change Password",
                          fontWeight: "font-semibold",
                        }}
                        className="text-primary xs:text-left !px-0"
                        onClick={() => {
                          history.replace("/forget-password");
                        }}
                      />
                      {IS_MFA_SOFT_LAUNCH_ENABLED && !mfaEnrolled && (
                        <>
                          <div className="hidden md:block h-8 border-r border-gray-300" />
                          <Button
                            buttonTheme="transparent"
                            textInformation={{
                              text: "Enable two-step verification",
                              fontWeight: "font-semibold",
                            }}
                            className="text-primary xs:text-left !px-0"
                            disabled={mfaLoading}
                            onClick={() => setShowMfaEnroll(true)}
                          />
                        </>
                      )}
                      {IS_PERSONAL_NOTIFICATION_SETTINGS_ENABLED &&
                        isPhysician && (
                          <>
                            <div className="hidden md:block h-8 border-r border-gray-300" />
                            <Button
                              buttonTheme="transparent"
                              textInformation={{
                                text: "Configure Personal Notifications & Reports Delivery",
                                fontWeight: "font-semibold",
                              }}
                              className="text-primary xs:text-left !px-0"
                              onClick={() => {
                                history.replace(
                                  "/access/notifications/settings/my-preferences"
                                );
                              }}
                            />
                          </>
                        )}
                    </div>
                  }
                />
              </div>

              {IS_MFA_SOFT_LAUNCH_ENABLED && (
                <div className="w-full border-t border-roundedInputBorder py-6 px-5">
                  <CustomText
                    text="Two-step verification"
                    fontWeight="font-semibold"
                  />
                  {enrolledMethodLabels.length > 0 ? (
                    <div className="my-3.5 flex flex-col gap-1">
                      {enrolledMethodLabels.map((label) => (
                        <CustomText
                          key={label}
                          text={`Enabled — ${label}.`}
                          fontWeight="font-normal"
                        />
                      ))}
                    </div>
                  ) : (
                    <CustomText
                      text={mfaStatusDescription}
                      fontWeight="font-normal"
                      className="my-3.5"
                    />
                  )}
                  {!mfaEnrolled && (
                    <Button
                      buttonTheme="primary"
                      textInformation={{
                        text: "Enable now",
                        fontWeight: "font-semibold",
                      }}
                      disabled={mfaLoading}
                      onClick={() => setShowMfaEnroll(true)}
                    />
                  )}
                  {mfaEnrolled && missingMfaMethod && (
                    <Button
                      buttonTheme="primary"
                      textInformation={{
                        text:
                          missingMfaMethod === "email"
                            ? "Add email"
                            : "Add authenticator app",
                        fontWeight: "font-semibold",
                      }}
                      disabled={mfaLoading}
                      onClick={() => setShowMfaEnroll(true)}
                    />
                  )}
                </div>
              )}
              {isPhysician && (
                <div className="w-full border-t border-b border-roundedInputBorder py-6 px-5">
                  <CustomText text={"E-Signature"} fontWeight="font-semibold" />
                  <CustomText
                    text={"E-signature is required for order authorization."}
                    fontWeight="font-normal"
                    className="my-3.5"
                  />
                  <div className="mb-3">
                    <SignatureViewer
                      imageURL={signatureData?.viewableURL ?? ""}
                      alt="Signature"
                      containerClassName="w-[300px]"
                      maxHeight="250px"
                      maxWidth="300px"
                    />
                  </div>
                  <div className="flex justify-start items-center">
                    <Button
                      textInformation={{
                        text: signatureData?.id
                          ? "Change Signature"
                          : "Create Signature",
                        fontWeight: "font-semibold",
                      }}
                      buttonTheme="transparent"
                      className="text-primary"
                      onClick={() => {
                        setShowSignaturePopup(true);
                      }}
                    />
                    {!!signatureData?.id && (
                      <Button
                        textInformation={{
                          text: "Delete Signature",
                          fontWeight: "font-semibold",
                        }}
                        disabled={creatingSignature || fetchingSignatureData}
                        buttonTheme="transparent"
                        className="text-requiredRed ml-3"
                        onClick={() => {
                          setShowDeletePopup(true);
                        }}
                      />
                    )}
                  </div>
                </div>
              )}
              {showDeletePopup && (
                <CustomPopUp
                  popUpTitle="E-Signature"
                  hidePopUp={() => setShowDeletePopup(false)}
                >
                  <CustomText
                    text={"Are you sure you want to delete the E-signature ?"}
                    fontWeight="font-normal"
                    className="text-requiredRed"
                  />
                  <div className="flex justify-end items-center mt-4">
                    <Button
                      textInformation={{
                        text: "No",
                        fontWeight: "font-semibold",
                      }}
                      buttonTheme="secondary"
                      disabled={deletingSignature}
                      onClick={() => {
                        setShowDeletePopup(false);
                      }}
                    />

                    <Button
                      textInformation={{
                        text: "Yes",
                        fontWeight: "font-semibold",
                      }}
                      disabled={deletingSignature}
                      loading={deletingSignature}
                      buttonTheme="primary"
                      className="ml-3"
                      onClick={() => {
                        deleteSignature();
                      }}
                    />
                  </div>
                </CustomPopUp>
              )}
              {showSignaturePopup && (
                <CustomPopUp
                  popUpTitle="E-Signature"
                  popUpSubtitle="E-signature is required for order authorization"
                  hidePopUp={() => setShowSignaturePopup(false)}
                >
                  <CustomSignatureInput
                    submitting={isPending || creatingSignature}
                    handleSubmission={handleSubmission}
                  />
                </CustomPopUp>
              )}
              {showPhoneModal && (
                <PhoneVerificationPopup
                  initialPhone={mobilePhone}
                  onClose={() => setShowPhoneModal(false)}
                  onVerified={() => setShowPhoneModal(false)}
                />
              )}
              {showMfaEnroll && (
                <MfaSetupPopup
                  onClose={() => setShowMfaEnroll(false)}
                  onEnrolled={() => setShowMfaEnroll(false)}
                  allowSkip
                  lockedMethod={
                    mfaEnrolled ? missingMfaMethod ?? undefined : undefined
                  }
                />
              )}
              <AssociatedOrganizationAndRolesTable
                tableContainerClass="xs:overflow-x-auto xs:w-[90vw] xs:border-r xs:border-t"
                userRoles={data?.user?.roles}
                data={
                  data?.organizationsRolesAndSOF
                    ? data.organizationsRolesAndSOF.map((org: any) => ({
                        ...org,
                        roles: utils.physicianToClinician(org.roles).join(","),
                      }))
                    : []
                }
                isLoading={isLoading}
              />
            </div>
          </div>
        )}
      </ErrorBoundary>
    </AdminContentLayout>
  );
}

export default Profile;
